/**
 * Config Slice
 *
 * Zustand slice for public application configuration.
 * Fetches and caches public config values from the API.
 *
 * @validates Requirements 8.7
 */

import type { StateCreator } from 'zustand';
import type { StoreState, ConfigSlice, ConfigState } from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Initial state for the config slice
 */
const initialState: ConfigState = {
  freeUserDailyQuota: 20, // Default value, will be overridden by API
  premiumMonthlyPrice: 4.99, // Default value for one-time payment, will be overridden by API
  premiumSubscriptionPrice: 0.99, // Default value for recurring subscription, will be overridden by API
  paypalClientId: '', // PayPal Client ID, will be loaded from API
  paypalPlanId: '', // PayPal Plan ID for recurring subscriptions
  maintenanceMode: false,
  configLoaded: false,
};

/**
 * Creates the config slice
 */
export const createConfigSlice: StateCreator<StoreState, [], [], ConfigSlice> = (
  set,
  get
) => ({
  ...initialState,

  setFreeUserDailyQuota: (quota: number) => {
    set({ freeUserDailyQuota: quota });
  },

  setPremiumMonthlyPrice: (price: number) => {
    set({ premiumMonthlyPrice: price });
  },

  setPremiumSubscriptionPrice: (price: number) => {
    set({ premiumSubscriptionPrice: price });
  },

  setPaypalClientId: (clientId: string) => {
    set({ paypalClientId: clientId });
  },

  setPaypalPlanId: (planId: string) => {
    set({ paypalPlanId: planId });
  },

  setMaintenanceMode: (enabled: boolean) => {
    set({ maintenanceMode: enabled });
  },

  loadPublicConfig: async (force?: boolean) => {
    // Don't reload if already loaded (unless force=true)
    if (get().configLoaded && !force) return;

    try {
      // Add timestamp to prevent browser caching
      const response = await fetch(`${API_BASE_URL}/config/public?_t=${Date.now()}`);
      const data = await response.json();
      
      if (data.success && data.data) {
        set({
          freeUserDailyQuota: data.data.freeUserDailyQuota ?? 20,
          premiumMonthlyPrice: data.data.premiumMonthlyPrice ?? 4.99,
          premiumSubscriptionPrice: data.data.premiumSubscriptionPrice ?? 0.99,
          paypalClientId: data.data.paypalClientId ?? '',
          paypalPlanId: data.data.paypalPlanId ?? '',
          maintenanceMode: data.data.maintenanceMode ?? false,
          configLoaded: true,
        });
      }
    } catch (error) {
      console.error('Failed to load public config:', error);
      // Keep default values on error
      set({ configLoaded: true });
    }
  },
});
